import { forwardRef, React, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled, { keyframes, css }  from 'styled-components';

import AccountInput from '../../../Component/AccountComponent/AccountInput.js';
import LogoFileInput from '../../../Component/AccountComponent/LogoFileInput.js';
import KakaoAddressInput from '../../../Component/AccountComponent/KakaoAddressInput.js';
import GoogleAddressInput from '../../../Component/AccountComponent/GoogleAddressInput.js';
import GoogleAddressModal from '../../Share/GoogleComponent/GoogleAddress.js';

import DefaultLogoPath from '../../../image/PlusButton.png';
import { useLanguage } from '../../../Context/LanguageContext';

const SignUpInfo4 = forwardRef((props, ref) => {
	const { kakaoAddrClick, isKorean } = props;
	const { t } = useLanguage();

	const [addressClickCount, setAddressClickCount] = useState(0);
	const [logoClickCount, setLogoClickCount] = useState(0);
	const [logoImage, setLogoImage] = useState(null);
	const [hasLogoFile, setHasLogoFile] = useState(false);
	const [isAnimate, setIsAnimate] = useState(false);
	const [isLogoDragging, setIsLogoDragging] = useState(false);

	const TopLevelRef = useRef();
	const AddressRef = useRef();
	const GoogleAddressRef = useRef();
	const LogoRef = useRef();
	const AddressDetailRef = useRef();
	const AddressDetailPlanationRef = useRef();
	const AddressPlanationRef = useRef();
	const GoogleAddressPlanationRef = useRef();
	const LogoPlanationRef = useRef();
	const GoogleAddressModalRef = useRef();

	useImperativeHandle(ref, () => ({
		enableEnterAnimation: () => {
			setIsAnimate(true);
		},
		disableEnterAnimation: () => {
			setIsAnimate(false);
		},
		getAddress: () => {
			const mainAddress = isKorean ?
				AddressRef.current?.getValue()
				:
				GoogleAddressRef.current?.getValue();

			return mainAddress + " " + AddressDetailRef.current.getValue();
		},
		getLogo: () => {
			return LogoRef.current.getImgBase64();
		},
		getLogoThumbnail: () => {
			return LogoRef.current.getImgBase64Thumbnail();
		},
		setAddressBold: () => {
			if (isKorean) {
				AddressRef.current?.setDefaultPlaceholder('');
				AddressPlanationRef.current.style.color = '#FF3131';
				AddressPlanationRef.current.style.opacity = 1;
			}
			else {
				GoogleAddressRef.current?.setDefaultPlaceholder('');
				GoogleAddressPlanationRef.current.style.color = '#FF3131';
				GoogleAddressPlanationRef.current.style.opacity = 1;
			}
		},
		setLogoBold: () => {
			LogoRef.current.setBold();
			LogoPlanationRef.current.style.color = '#FF3131';
			LogoPlanationRef.current.style.opacity = 1;
		},
		setAddress: (val) => {
			if (isKorean) {
				AddressRef.current?.setValue(val);
			}
			else {
				GoogleAddressRef.current?.setValue(val);
			}
			setAddressClickCount(addressClickCount + 1);
		},
		setOpacity: (val) => {
			TopLevelRef.current.style.opacity = val;
			if (val === 0) TopLevelRef.current.setAttribute('inert', '');
			else TopLevelRef.current.removeAttribute('inert');
		},
		setLocation: (val) => {
			TopLevelRef.current.style.transform = `translateX(${val}%)`;
		},
		reset: () => {
		}
	}));

	useEffect(() => {
		TopLevelRef.current.setAttribute('inert', '');
	}, []);

	useEffect(() => {
		if (addressClickCount === 0 && logoClickCount === 0) return;

		if (isKorean) {
			if (AddressRef.current?.getValue() === undefined && addressClickCount > 0) {
				AddressPlanationRef.current.style.color = '#FF3131';
				AddressPlanationRef.current.style.opacity = 1;
			}
			else {
				AddressPlanationRef.current.style.opacity = 0;
			}
		}
		else {
			if (GoogleAddressRef.current?.getValue() === '' && addressClickCount > 0) {
				GoogleAddressPlanationRef.current.style.color = '#FF3131';
				GoogleAddressPlanationRef.current.style.opacity = 1;
			}
			else {
				GoogleAddressPlanationRef.current.style.opacity = 0;
			}
		}

		if (LogoRef.current.getValue() === '' && logoClickCount > 0) {
			LogoPlanationRef.current.style.color = '#FF3131';
			LogoPlanationRef.current.style.opacity = 1;
		}
		else {
			LogoPlanationRef.current.style.opacity = 0;
		}
	}, [addressClickCount, logoClickCount, isKorean])

	function openKakaoAddress() {
		const el = TopLevelRef.current;
		const prev = el.style.transform;

		// transform 제거 (fixed 계산 정상화)
		el.style.transform = 'none';

		// 한 틱 뒤 모달 열기
		setTimeout(() => {
			kakaoAddrClick(); // Kakao 주소 모달 열기
			// 다시 원복
			el.style.transform = prev;
		}, 0);
	}

	function openGoogleAddress() {
		const el = TopLevelRef.current;
		const prev = el.style.transform;

		// transform 제거 (fixed 계산 정상화)
		el.style.transform = 'none';

		// 한 틱 뒤 모달 열기
		setTimeout(() => {
			GoogleAddressModalRef.current.setOpacity(1); // Google 주소 모달 열기
			// 다시 원복
			el.style.transform = prev;
		}, 0);
	}

	function exitGoogleAddress(selectedAddress) {
		GoogleAddressModalRef.current.setOpacity(0);

		if (selectedAddress) {
			GoogleAddressRef.current.setValue(selectedAddress);
		}
	}

	function clickCountFn(className, clickCount) {
		if (className === 'AddressInput' || className === 'GoogleAddressInput') {
			setAddressClickCount(clickCount);
		}
		else if (className === 'LogoInput') {
			setLogoClickCount(clickCount);
		}
	}

	function handleImageChange(imgBase64, imgFile) {
		setLogoImage(imgBase64);
		setHasLogoFile(!!imgFile);
	}

	function handleLogoWrapperClick() {
		LogoRef.current?.triggerFileSelect();
	}

	function handleDragStateChange(isDragging) {
		setIsLogoDragging(isDragging);
	}

	function handleLogoDragEnter(e) {
		e.preventDefault();
		e.stopPropagation();
		setIsLogoDragging(true);
	}

	function handleLogoDragOver(e) {
		e.preventDefault();
		e.stopPropagation();
	}

	function handleLogoDragLeave(e) {
		e.preventDefault();
		e.stopPropagation();

		const rect = e.currentTarget.getBoundingClientRect();
		const x = e.clientX;
		const y = e.clientY;

		if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
			setIsLogoDragging(false);
		}
	}

	function handleLogoDrop(e) {
		e.preventDefault();
		e.stopPropagation();
		setIsLogoDragging(false);

		const files = e.dataTransfer.files;

		if (files && files.length > 0) {
			const file = files[0];
			const fileName = file.name;
			LogoRef.current?.processFile(file, fileName);
		}
	}

	return (
		<>
			<TopLevelWrapper ref={TopLevelRef} $animate={isAnimate}>
				{isKorean ? (
					<>
						<InputInfo>{t('signUp.address')}</InputInfo>
						<KakaoAddressInput ref={AddressRef} className='AddressInput' placeholder={t('signUp.addressPlaceholder')} type='text' clickCountFn={clickCountFn} clicked={openKakaoAddress} />
						<AccountExplanation ref={AddressPlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
					</>
				) : (
					<>
						<InputInfo>{t('signUp.address')}</InputInfo>
						<GoogleAddressInput ref={GoogleAddressRef} className='GoogleAddressInput' placeholder={t('signUp.addressPlaceholder')} type='text' clickCountFn={clickCountFn} clicked={openGoogleAddress} />
						<AccountExplanation ref={GoogleAddressPlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
					</>
				)}
				<InputInfo>{t('signUp.addressDetail')}</InputInfo>
				<AccountInput ref={AddressDetailRef} className='AddressDetailInput' placeholder={t('signUp.addressDetailPlaceholder')} type='text' clickCountFn={clickCountFn}/>
				<AccountExplanation ref={AddressDetailPlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
				<LogoTopLevelWrapper>
					<LogoExplanation>
						<InputInfo>{t('signUp.centerLogo')}</InputInfo>
						<LogoFileInput
							ref={LogoRef}
							className='LogoInput'
							clickCountFn={clickCountFn}
							onImageChange={handleImageChange}
							isDragging={isLogoDragging}
							onDragStateChange={handleDragStateChange}
						/>
						<AccountExplanation ref={LogoPlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
					</LogoExplanation>
					<LogoWrapper
						onClick={handleLogoWrapperClick}
						onDragEnter={handleLogoDragEnter}
						onDragOver={handleLogoDragOver}
						onDragLeave={handleLogoDragLeave}
						onDrop={handleLogoDrop}
						$isDragging={isLogoDragging}
					>
						{hasLogoFile && <Logo src={logoImage} />}
						<PlusIcon src={DefaultLogoPath} $hasImage={hasLogoFile} />
					</LogoWrapper>
				</LogoTopLevelWrapper>
			</TopLevelWrapper>
			{!isKorean && <GoogleAddressModal ref={GoogleAddressModalRef} exitClick={exitGoogleAddress} />}
		</>
	)
})

export default SignUpInfo4;

const startAnimation = keyframes`
	0% { opacity: 0; transform: translateX(150%); }
	10% { opacity: 0.1; transform: translateX(130%); }
	20% { opacity: 0.2; transform: translateX(100%); }
	30% { opacity: 0.3; transform: translateX(70%); }
	40% { opacity: 0.4; transform: translateX(40%); }
	50% { opacity: 0.5; transform: translateX(20%); }
	60% { opacity: 0.6; transform: translateX(0%); }
	70% { opacity: 0.7;}
	80% { opacity: 0.8; }
	90% { opacity: 0.9;}
	100% { opacity: 1;}
`;

const TopLevelWrapper = styled.div`
	position: absolute;
	display: flex;

	flex-direction: column;
	justify-content: flex-start;

	width: 480px;
	// width: max(271px, 24.375vw);
	// height: 281px;

	margin-top: 147px;
	// margin-top: max(100px, 8.28125vw);

	// background-color: rgba(255, 255, 255, 1);

	opacity: 0;
	z-index: 1;
	transform: translateX(150%);

	animation: none;

	${(props) =>
			props.$animate &&
			css`
				animation: ${startAnimation} 0.5s ease-out;
	`}

	transition: all 0.3s ease-in-out;
`;

const InputInfo = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 16px;
	// font-size: max(12px, 0.7291666666666667vw);
	// line-height: 20px;
 
	margin: 19px 0 0 0;
	// margin-top: max(11.73px, 0.9895833333333333vw);
	// margin-right: 0px;
	// margin-bottom: 0px;
	// margin-left: 0px;

	cursor: default;
`;

const AccountExplanation = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;
	// font-size: max(12px, 0.625vw);
	// line-height: 17.38px;

	color: ${props => props.bold ? `#FF3131` : `#33C8FF`};

	margin: 4px 0 0 0;
	opacity: 0;
  transition: all 0.5s ease-in-out;
`;

const LogoTopLevelWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: space-between;
	width: 480px;
`;

const LogoExplanation = styled.div`
	display: flex;
	flex-direction: column;
`;

const LogoWrapper = styled.div`
	position: relative;
	display: flex;

	width: 97px;
	height: 97px;

	margin-top: 16px;
	margin-left: 20px;

	justify-content: center;
	align-items: center;

	border-radius: 10px;
	background-color: #EFEFEF;
	border: 2px solid ${props => props.$isDragging ? '#FF8B48' : 'transparent'};
	overflow: hidden;

	cursor: pointer;
	transition: border-color 0.3s ease-in-out;

	&:hover {
		border-color: #FF8B48;
	}
`;


const Logo = styled.img`
	position: absolute;
	top: 0;
	left: 0;
	width: 97px;
	height: 97px;

	object-fit: cover;
`;

const PlusIcon = styled.img`
	position: absolute;
	width: 40px;
	height: 40px;

	object-fit: contain;
	opacity: ${props => props.$hasImage ? 0 : 1};
	transition: opacity 0.3s ease-in-out;
	pointer-events: none;

	${LogoWrapper}:hover & {
		opacity: 1;
	}
`;